// InputBlock — collects one field of operator-provided data into the host's
// form-state store. Three flavours (text / email / textarea) share the same
// render shell; the type prop picks the input element.
//
// The block's `name` prop becomes the key the submit block reads. `formId`
// defaults to "default" — operator overrides when one slot hosts multiple
// forms (rare).

import {
  resolveContentSource,
  type ContentSource,
  type ContentSourceContext,
} from "./contentSource";
import type { BlockHostContext, BlockUniversalProps } from "./types";

export interface InputBlockProps extends BlockUniversalProps {
  host: BlockHostContext;
  /** Field id the submit block reads. Required — empty `name` skips render. */
  name: string;
  /** `text` / `email` / `textarea` (matches the block kind). */
  inputType: "text" | "email" | "textarea";
  label?: string;
  placeholder?: string;
  /** Initial value if the store is empty. */
  defaultValue?: string;
  /** Mark required so submit validation rejects empties. */
  required?: boolean;
  /** Textarea row hint. */
  rows?: number;
  /** Form id the field writes to. Defaults to "default" so a single form per
   *  slot just works without an operator setting it. */
  formId?: string;
  /** Content source for the label (literal / dvar / from-*). */
  labelSource?: ContentSource;
  /** Content source for the placeholder (literal / dvar / from-*). */
  placeholderSource?: ContentSource;
  /** Live content-source context (series + selection), handed down by Slot. */
  sourceCtx?: ContentSourceContext;
}

export function InputBlock({
  host, name, inputType, label, placeholder, defaultValue,
  required, rows, formId, themeVariant, className,
  labelSource, placeholderSource, sourceCtx,
}: InputBlockProps) {
  if (!name) {
    console.warn("[InputBlock] missing `name` prop; block skipped.");
    return null;
  }
  const fid = formId || "default";
  const stored = host.formValues?.[fid]?.[name];
  const value = stored ?? defaultValue ?? "";

  const resolvedLabel = resolveContentSource(labelSource, label, sourceCtx ?? {});
  const resolvedPlaceholder = resolveContentSource(placeholderSource, placeholder, sourceCtx ?? {}) ?? undefined;
  const onChange = (next: string) => host.setFormValue(fid, name, next);
  const labelEl = resolvedLabel ? <span className="sd-input__label">{resolvedLabel}</span> : null;
  const cls = "sd-input" + (themeVariant ? ` sd-input--${themeVariant}` : "") + (className ? " " + className : "");

  if (inputType === "textarea") {
    return (
      <label className={cls} data-form={fid} data-name={name}>
        {labelEl}
        <textarea
          className="sd-input__control sd-input__control--textarea"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={resolvedPlaceholder}
          rows={rows ?? 4}
          required={required}
        />
      </label>
    );
  }

  return (
    <label className={cls} data-form={fid} data-name={name}>
      {labelEl}
      <input
        className="sd-input__control"
        type={inputType === "email" ? "text" : "text"}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={resolvedPlaceholder}
        required={required}
        autoComplete="off"
      />
    </label>
  );
}
